<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable">
      <div class="rd-chips" role="group" :aria-label="$t('order_status')">
        <button
          v-for="chip in statusChips"
          :key="chip.value"
          type="button"
          class="rd-chip"
          :aria-pressed="String(orderStatus === chip.value)"
          @click="orderStatus = chip.value"
        >
          {{ chip.label }}
          <b class="rd-num">{{ chip.count }}</b>
        </button>
      </div>
    </report-tools>

    <report-state
      v-if="!selectedCompany"
      icon="mdi-domain"
      :title="$t('report_mobile.employees_pick_company')"
    ></report-state>
    <report-state v-else-if="loading && !report" kind="loading"></report-state>
    <report-state v-else-if="!rows.length" icon="mdi-account-off-outline" :title="emptyText"></report-state>

    <div v-else class="rd-card rd-card--scroll">
      <table class="rd-tbl rd-tbl--workers">
        <thead>
          <tr>
            <th scope="col" class="rd-col-id" :aria-sort="ariaSort('id')">
              <button type="button" @click="sortBy('id')">ID {{ sortArrow("id") }}</button>
            </th>
            <th scope="col" :aria-sort="ariaSort('name')">
              <button type="button" @click="sortBy('name')">{{ $t("worker") }} {{ sortArrow("name") }}</button>
            </th>
            <th v-if="hasExternalCode" scope="col" :aria-sort="ariaSort('externalCode')">
              <button type="button" @click="sortBy('externalCode')">
                {{ $t("external_code") }} {{ sortArrow("externalCode") }}
              </button>
            </th>
            <th
              v-if="isSingleDay"
              scope="col"
              class="rd-col-time"
              :title="$t('order_datetime')"
              :aria-sort="ariaSort('sortTime')"
            >
              <button type="button" @click="sortBy('sortTime')">{{ $t("time") }} {{ sortArrow("sortTime") }}</button>
            </th>
            <th
              v-for="shift in visibleShifts"
              :key="shift.id"
              scope="col"
              class="is-center"
              :aria-sort="ariaSort(shift.key)"
            >
              <button type="button" @click="sortBy(shift.key)">
                <report-shift-tag :shift="shift.id"></report-shift-tag>
                {{ sortArrow(shift.key) }}
              </button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('ordered')">
              <button type="button" @click="sortBy('ordered')">{{ $t("ordered") }} {{ sortArrow("ordered") }}</button>
            </th>
            <th scope="col" class="is-right rd-col-taken" :aria-sort="ariaSort('taken')">
              <button type="button" @click="sortBy('taken')">{{ $t("taken") }} {{ sortArrow("taken") }}</button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('difference')">
              <button type="button" @click="sortBy('difference')">
                {{ $t("difference") }} {{ sortArrow("difference") }}
              </button>
            </th>
            <th scope="col" :aria-sort="ariaSort('stateRank')">
              <button type="button" @click="sortBy('stateRank')">{{ $t("status") }} {{ sortArrow("stateRank") }}</button>
            </th>
            <th scope="col" class="rd-col-go"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.key"
            data-open
            tabindex="0"
            :class="{ 'is-selected': isOpen(row), 'is-idle': row.state === 'off' }"
            @click="openWorker(row, $event)"
            @keydown.enter.prevent="openWorker(row, $event)"
            @keydown.space.prevent="openWorker(row, $event)"
          >
            <td class="rd-col-id rd-num rd-muted">{{ row.id }}</td>
            <td class="is-name" :title="row.name">
              <span class="rd-person">
                <span class="rd-avatar rd-avatar--sm">{{ row.initials }}</span>
                <span class="rd-person__name">{{ row.name }}</span>
              </span>
            </td>
            <td v-if="hasExternalCode" class="rd-num rd-muted">{{ row.externalCode || "–" }}</td>
            <td v-if="isSingleDay" class="rd-col-time rd-num rd-muted">{{ row.time || "–" }}</td>
            <td v-for="shift in visibleShifts" :key="shift.id" class="is-center">
              <span v-if="row[shift.key]" class="rs-tag rs-tag--count" :data-shift="shift.id">
                {{ row[shift.key] }}
                <span class="rs-tag__sr">{{ $t("report_mobile.shift_n", { n: shift.id }) }}</span>
              </span>
              <template v-else>
                <span class="rd-dash" aria-hidden="true">–</span>
                <span class="rd-visually-hidden">0</span>
              </template>
            </td>
            <td class="is-right rd-num" :class="{ 'is-zero': !row.ordered }">{{ row.ordered }}</td>
            <td class="is-right rd-num rd-col-taken" :class="{ 'is-zero': !row.taken }">{{ row.taken }}</td>
            <td class="is-right rd-num" :class="{ 'is-zero': row.difference === 0, 'is-alert': row.difference > 0 }">
              {{ row.difference }}
            </td>
            <td>
              <span class="rd-status" :class="`rd-status--${row.state}`">{{ row.statusLabel }}</span>
            </td>
            <td class="rd-col-go"><v-icon size="18" color="#8a817a">mdi-chevron-right</v-icon></td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td class="rd-col-id"></td>
            <td>{{ $t("total") }}</td>
            <td v-if="hasExternalCode"></td>
            <td v-if="isSingleDay" class="rd-col-time"></td>
            <td v-for="shift in visibleShifts" :key="shift.id" class="is-center rd-num">{{ sums[shift.key] }}</td>
            <td class="is-right rd-num">{{ sums.ordered }}</td>
            <td class="is-right rd-num rd-col-taken">{{ sums.taken }}</td>
            <td class="is-right rd-num">{{ sums.difference }}</td>
            <td></td>
            <td class="rd-col-go"></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import ReportMealsTabEmployees from "@/components/report/more-restaurants/tabs/ReportMealsTabEmployees.vue";
import hasShiftBinding from "../../../shared/hasShiftBinding";
import hasShiftSubtitle from "../../../shared/hasShiftSubtitle";
import hasMoreEmployeeRows from "../../../shared/more/hasMoreEmployeeRows";
import ReportShiftTag from "../../../shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "../../copyTable";
import hasTableSort from "../../hasTableSort";
import ReportState from "../../ReportState.vue";
import ReportTools from "../../ReportTools.vue";
import ReportDesktopWorkerMonth from "../ReportDesktopWorkerMonth.vue";

/**
 * Radnici firme "više restorana" na računaru: spisak radnika izabrane firme sa smenama u kojima su poručili,
 * poručeno / preuzeto / razlikom i statusom. Filter statusa su čipovi sa brojevima; klik na red otvara mesečni
 * pregled radnika (kod svih restorana) u panelu. Firmu, smene i period čita iz zajedničke trake. Nasleđuje
 * učitavanje, filter statusa, štampu i Excel iz starog taba.
 */
export default {
  name: "ReportDesktopMoreEmployees",
  extends: ReportMealsTabEmployees,
  mixins: [hasShiftBinding, hasShiftSubtitle, hasMoreEmployeeRows, hasTableSort],
  components: { ReportShiftTag, ReportState, ReportTools },
  inject: {
    shell: { from: "reportShell", default: null },
  },
  data: () => ({
    sort: { key: "name", dir: 1 },
  }),
  computed: {
    rows() {
      return this.sortRows(this.matchedRows);
    },
    kpis() {
      return [
        { key: "ordered", label: this.$t("ordered"), value: this.sums.ordered, tone: "inverse" },
        { key: "taken", label: this.$t("taken"), value: this.sums.taken },
        { key: "difference", label: this.$t("difference"), value: this.sums.difference },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    isOpen(row) {
      const top = this.shell?.panelStack?.[0];
      return Boolean(top && top.key === `worker-${row.id}`);
    },
    /** Mesečni pregled radnika u panelu (isti podaci kao stari dijalog: "more-restaurants-orders-by-month"). */
    openWorker(row, event) {
      const employee = this.toEmployeeContext(row);
      this.$emit("open-panel", {
        opener: event?.currentTarget,
        key: `worker-${row.id}`,
        root: true,
        wide: true,
        title: row.name,
        subtitle: [`ID ${row.id}`, employee.companyName].filter(Boolean).join(" · "),
        component: ReportDesktopWorkerMonth,
        props: { compact: true, worker: employee },
      });
    },
    async copyTable() {
      const headers = [
        "ID",
        this.$t("worker"),
        ...(this.hasExternalCode ? [this.$t("external_code")] : []),
        ...(this.isSingleDay ? [this.$t("time")] : []),
        ...this.visibleShifts.map((shift) => this.$t(shift.label)),
        this.$t("ordered"),
        this.$t("taken"),
        this.$t("difference"),
        this.$t("status"),
      ];
      const rows = this.rows.map((row) => [
        row.id,
        row.name,
        ...(this.hasExternalCode ? [row.externalCode] : []),
        ...(this.isSingleDay ? [row.time] : []),
        ...this.visibleShifts.map((shift) => row[shift.key]),
        row.ordered,
        row.taken,
        row.difference,
        row.statusLabel,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
